import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import feedbackService from '../../utils/feedbackService';
import { 
  MessageCircle, 
  FileText, 
  Star, 
  Briefcase, 
  Lightbulb, 
  Bug 
} from 'lucide-react';

const FeedbackForm = ({ 
  type = 'general', 
  contextId = null, 
  contextTitle = null, 
  onSubmit, 
  onCancel 
}) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    rating: 0,
    comment: '',
    type: type
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const vaFeedbackTypes = {
    general: { title: 'General Feedback', icon: MessageCircle, requiresRating: false },
    job_application: { title: 'Job Application Experience', icon: FileText, requiresRating: true },
    interview: { title: 'Interview Experience', icon: Star, requiresRating: true },
    employer_interaction: { title: 'Employer Interaction', icon: Briefcase, requiresRating: true },
    feature_request: { title: 'Feature Request', icon: Lightbulb, requiresRating: false },
    bug_report: { title: 'Bug Report', icon: Bug, requiresRating: false }
  };

  const employerFeedbackTypes = {
    general: { title: 'General Feedback', icon: MessageCircle, requiresRating: false },
    hiring_experience: { title: 'Hiring Experience', icon: Briefcase, requiresRating: true },
    candidate_interaction: { title: 'Candidate Interaction', icon: FileText, requiresRating: true },
    platform_experience: { title: 'Platform Experience', icon: Star, requiresRating: true },
    feature_request: { title: 'Feature Request', icon: Lightbulb, requiresRating: false },
    bug_report: { title: 'Bug Report', icon: Bug, requiresRating: false }
  };

  const feedbackTypes = userProfile?.role === 'employer' ? employerFeedbackTypes : vaFeedbackTypes;

  const validateForm = () => {
    const newErrors = {};
    const currentType = feedbackTypes[formData.type];

    if (currentType?.requiresRating && formData.rating === 0) {
      newErrors.rating = 'Please provide a rating';
    }

    if (!formData.comment.trim()) {
      newErrors.comment = 'Please provide your feedback';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const feedbackData = {
        userId: userProfile.id,
        type: formData.type,
        rating: formData.rating || null,
        comment: formData.comment,
        contextData: {
          contextId,
          contextTitle,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      };

      const result = await feedbackService.submitFeedback(feedbackData);
      
      if (result.success) {
        if (onSubmit) {
          onSubmit(result.data);
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setErrors({ submit: 'Failed to submit feedback. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentTypeInfo = feedbackTypes[formData.type];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            {currentTypeInfo?.icon && <currentTypeInfo.icon size={20} />}
            <h2 className="text-xl font-bold text-gray-900">
              {currentTypeInfo?.title}
            </h2>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {contextTitle && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <span className="font-medium">Context:</span> {contextTitle}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Feedback Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Feedback Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(feedbackTypes).map(([key, info]) => (
                <option key={key} value={key}>
                  {info.title}
                </option>
              ))}
            </select>
          </div>

          {/* Rating (if required) */}
          {currentTypeInfo?.requiresRating && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rating *
              </label>
              <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className={`text-2xl transition-colors ${
                      star <= formData.rating ? 'text-yellow-400' : 'text-gray-300'
                    } hover:text-yellow-400`}
                  >
                    ⭐
                  </button>
                ))}
                <span className="ml-2 text-sm text-gray-600">
                  {formData.rating > 0 ? `${formData.rating}/5` : 'No rating'}
                </span>
              </div>
              {errors.rating && (
                <p className="mt-1 text-sm text-red-600">{errors.rating}</p>
              )}
            </div>
          )}

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Feedback *
            </label>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              placeholder="Please share your thoughts, suggestions, or describe any issues you encountered..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            {errors.comment && (
              <p className="mt-1 text-sm text-red-600">{errors.comment}</p>
            )}
          </div>

          {errors.submit && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center space-x-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;
